import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTION_LABELS = {
    admin_approved: { label: "Admin approved", tint: "success" },
    admin_rejected: { label: "Admin rejected", tint: "error" },
    changes_requested: { label: "Changes requested", tint: "warning" },
    restored: { label: "Restored", tint: "info" },
    retry_ai: { label: "AI retried", tint: "info" },
    doctor_approved: { label: "Doctor approved", tint: "success" },
    profile_published: { label: "Profile published", tint: "success" },
    publication_restored: { label: "Version restored", tint: "info" },
    payment_successful: { label: "Payment successful", tint: "success" },
    payment_failed: { label: "Payment failed", tint: "error" },
    invoice_generated: { label: "Invoice generated", tint: "info" },
    credential_change_post_publish: { label: "Credential edit (post-publish)", tint: "warning" },
    ai_job_retried: { label: "AI job requeued", tint: "info" },
    domain_requested: { label: "Domain requested", tint: "info" },
    domain_approved: { label: "Domain approved", tint: "success" },
    domain_rejected: { label: "Domain rejected", tint: "error" },
};

function chip(tint) {
    return {
        success: "ddi-chip ddi-chip-success",
        error: "ddi-chip ddi-chip-error",
        warning: "ddi-chip ddi-chip-warning",
        info: "ddi-chip ddi-chip-primary",
    }[tint] || "ddi-chip ddi-chip-neutral";
}


export default function AdminActivity() {
    const [rows, setRows] = useState([]);
    const [days, setDays] = useState(14);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get(`/admin/activity?days=${days}&limit=200`);
            setRows(r.data.items || []);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [days]);

    return (
        <AdminShell>
            <div className="mb-6 flex items-center justify-between" data-testid="admin-activity-header">
                <div>
                    <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Activity</h1>
                    <p className="text-[#64748B] mt-1 text-[14px]">Recent workflow events across the platform.</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[12px] text-[#64748B]">Range</span>
                    <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                        <SelectTrigger className="w-32" data-testid="admin-activity-days"><SelectValue /></SelectTrigger>
                        <SelectContent>{[3, 7, 14, 30, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={load} disabled={loading} className="ddi-btn-outline">
                        <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            <Card className="border-[#E5E9F2] ddi-shadow-sm">
                <CardContent className="py-0">
                    {rows.length === 0 ? (
                        <div className="py-16 text-center">
                            <FileText size={40} className="mx-auto text-[#94A3B8] mb-3" />
                            <div className="text-[13px] text-[#64748B]">No workflow activity in this range.</div>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#EEF1F6]" data-testid="admin-activity-list">
                            {rows.map((r) => {
                                const meta = ACTION_LABELS[r.action] || { label: r.action, tint: "neutral" };
                                return (
                                    <li key={r.id} className="py-3 px-4 flex items-start gap-3" data-testid={`activity-row-${r.id}`}>
                                        <span className={chip(meta.tint)}>{meta.label}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] text-[#0F172A]">
                                                {r.doctor ? (
                                                    <Link to={`/admin/review/${r.doctor.id}`} className="font-semibold hover:underline">
                                                        Dr. {r.doctor.full_name}
                                                    </Link>
                                                ) : (
                                                    <span className="font-semibold">Platform action</span>
                                                )}
                                                <span className="text-[#64748B] ml-1">
                                                    · by {r.actor_email || r.actor_role || "system"}
                                                </span>
                                            </div>
                                            {r.new_value && (
                                                <div className="text-[11px] text-[#94A3B8] font-mono mt-0.5 line-clamp-2">
                                                    {typeof r.new_value === "object" ? JSON.stringify(r.new_value) : String(r.new_value)}
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">
                                            {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </AdminShell>
    );
}
